<script setup lang="ts">
import { BsError, BsSelect, BsCheckbox } from '@afo/bsf-core'
import { useAgreements } from '@/domains/partners/multisport/composables/useAgreements'
import { useDistricts } from '@/domains/partners/multisport/composables/useDistricts'
import { useObjectCategories } from '@/domains/partners/multisport/composables/useObjectCategories'
import { usePhonePrefixes } from '@/domains/partners/multisport/composables/usePhonePrefixes'
import { useMultisportPartnersStore } from '@/domains/partners/multisport/store/useMultiSportPartnersStore'
import { useHasRadios } from '~/domains/partners/multisport/const/HasRadios.const'
import Recaptcha from '~/domains/recaptcha/components/Recaptcha/Recaptcha.vue'
import TextInput from '@/components/Inputs/TextInput/TextInput.vue'
import NumericInput from '@/components/Inputs/NumericInput/NumericInput.vue'

const { t } = useI18n()
const { form, $v} = useMultisportPartnersStore()
const { isDesktop } = useBSBreakpoints()
const { hasOc, hasPermits, hasSafetyEquipment, hasReception, isAutonomous } = useHasRadios()
const { checkboxesMain, checkboxesDetails, checkboxesTerms, setAllAgreements, allAgreementsSet } = useAgreements()
const districts = useDistricts()
const objectCategories = useObjectCategories()
const phonePrefixes = usePhonePrefixes()
const checkAll = ref(false)

const onToggleObjectCategory = () => {
  if (form.objectCategory.value !== 'Inne') {
    form.objectOtherCategory = ''
  }
}

</script>

<template>
  <div class="stepThree">
    <h1 class="stepThree__title">
      {{ $t('partners.multisport.steps.step3.subTitle') }}
    </h1>

    <div class="stepThree__form">
      <div>
        <TextInput
          id="objectName"
          v-model="form.objectName"
          name="objectName"
          :label="`${t('form.objectName')} *`"
          :error-message="$v.objectName.$errors[0]?.$message"
          :error="!!$v.objectName.$errors[0]?.$message"
          :disabled="false"
          :required="true"
          @blur="$v.objectName.$touch()"
        />
      </div>

      <div>
        <NumericInput
          id="objectArea"
          v-model="form.objectArea"
          name="objectArea"
          :label="`${t('form.objectArea')} *`"
          :error-message="$v.objectArea.$errors[0]?.$message"
          :error="!!$v.objectArea.$errors[0]?.$message"
          @blur="$v.objectArea.$touch()"
        />
      </div>

      <div class="stepThree__country">
        <div :class="{ stepThree__half: isDesktop }">
          <BsError :error="!!$v.objectDistrict.$errors[0]?.$message">
            <BsSelect
              v-model="form.objectDistrict"
              :label="`${t('form.district.name')} *`"
              :error="!!$v.objectDistrict.$errors[0]?.$message"
              :options="districts"
            />
          </BsError>
        </div>
      </div>

      <div class="stepThree__street">
        <div :class="isDesktop ? 'stepThree__quarter' : 'stepThree__half'">
          <TextInput
            id="street"
            v-model="form.objectStreet"
            name="street"
            :label="`${t('form.street')} *`"
            :error-message="$v.objectStreet.$errors[0]?.$message"
            :error="!!$v.objectStreet.$errors[0]?.$message"
            :required="true"
            @blur="$v.objectStreet.$touch()"
          />
        </div>

        <div
          :class="[
            'stepThree__buildingNumber',
            isDesktop ? 'stepThree__twoThirds' : 'stepThree__half',
          ]"
        >
          <div :class="isDesktop && 'stepThree__half'">
            <TextInput
              id="buildingNumber"
              v-model="form.objectBuildingNumber"
              name="buildingNumber"
              :label="`${t('form.buildingNumber')} *`"
              :error-message="$v.objectBuildingNumber.$errors[0]?.$message"
              :error="!!$v.objectBuildingNumber.$errors[0]?.$message"
              :required="true"
              @blur="$v.objectBuildingNumber.$touch()"
            />
          </div>

          <div
            v-if="isDesktop"
            class="stepThree__half"
          >
            <TextInput
              id="objectApartmentNumber"
              v-model="form.objectApartmentNumber"
              name="objectApartmentNumber"
              :label="`${t('form.apartmentNumber')}`"
              :error-message="$v.objectApartmentNumber.$errors[0]?.$message"
              :error="!!$v.objectApartmentNumber.$errors[0]?.$message"
              :required="false"
              @blur="$v.objectApartmentNumber.$touch()"
            />
          </div>
        </div>
      </div>

      <div v-if="!isDesktop">
        <TextInput
          id="objectApartmentNumber"
          v-model="form.objectApartmentNumber"
          name="objectApartmentNumber"
          :label="`${t('form.apartmentNumber')}`"
          :error-message="$v.objectApartmentNumber.$errors[0]?.$message"
          :error="!!$v.objectApartmentNumber.$errors[0]?.$message"
          :required="false"
          @blur="$v.objectApartmentNumber.$touch()"
        />
      </div>

      <div class="stepThree__city">
        <div class="stepThree__half">
          <TextInput
            id="objectCity"
            v-model="form.objectCity"
            name="objectCity"
            :label="`${t('form.city')} *`"
            :error-message="$v.objectCity.$errors[0]?.$message"
            :error="!!$v.objectCity.$errors[0]?.$message"
            :required="true"
            @blur="$v.objectCity.$touch()"
          />
        </div>

        <div class="stepThree__half">
          <TextInput
            id="objectZipCode"
            v-model="form.objectZipCode"
            name="objectZipCode"
            :label="`${t('form.zipCode')}`"
            :error-message="$v.objectZipCode.$errors[0]?.$message"
            :error="!!$v.objectZipCode.$errors[0]?.$message"
            :required="true"
            @blur="$v.objectZipCode.$touch()"
          />
        </div>
      </div>

      <div class="stepThree__phones">
        <div class="stepThree__half">
          <BsError :message="$v.objectPrefixPhone.$errors[0]?.$message">
            <BsSelect
              v-model="form.objectPrefixPhone"
              :label="`${t('form.phonePrefix.name')} *`"
              :placeholder="$t('form.phonePrefix.placeholder')"
              :options="phonePrefixes"
              :error="!!$v.objectPrefixPhone.$errors[0]?.$message"
            />
          </BsError>
        </div>

        <div class="stepThree__half">
          <TextInput
            id="objectPhone"
            v-model="form.objectPhone"
            type="tel"
            name="objectPhone"
            :label="`${t('form.phoneWithoutPrefix')} *`"
            :error-message="$v.objectPhone.$errors[0]?.$message"
            :error="!!$v.objectPhone.$errors[0]?.$message"
            :disabled="form.objectPrefixPhone ? false : true"
            :required="true"
            @blur="$v.objectPhone.$touch()"
          />
        </div>
      </div>

      <div>
        <TextInput
          id="website"
          v-model="form.objectWebiste"
          name="objectWebiste"
          :error-message="$v.objectWebiste.$errors[0]?.$message"
          :error="!!$v.objectWebiste.$errors[0]?.$message"
          :disabled="false"
          :required="true"
          :label="$t('form.website') + '*'"
          @blur="$v.objectWebiste.$touch()"
        />
      </div>

      <BsError :message="$v.objectCategory.$errors[0]?.$message">
        <BsSelect
          v-model="form.objectCategory"
          :label="`${t('form.category.name')} *`"
          :error="!!$v.objectCategory.$errors[0]?.$message"
          :options="objectCategories"
          dropdown-height="32rem"
          @update:model-value="onToggleObjectCategory"
        />
      </BsError>
      
      <div>
        <TextInput
          id="objectOtherCategory"
          :key="form.objectCategory"
          v-model="form.objectOtherCategory"
          :error="!!$v.objectOtherCategory.$errors[0]?.$message"
          :error-message="$v.objectOtherCategory.$errors[0]?.$message"
          :disabled="form.objectCategory.value === 'Inne' ? false : true"
          name="objectOtherCategory"
          :label="$t('form.otherCategory')"
          @blur="$v.objectOtherCategory.$touch()"
        />
      </div>

      <div class="stepThree__radio">
        <p class="stepThree__radio-text">
          {{ $t('partners.multisport.steps.step3.hasOC') }}
        </p>

        <RadioButtonController
          v-model="form.objectHasOC"
          :radio-controls="hasOc"
          :error="!!$v.objectHasOC.$errors[0]?.$message"
        />
      </div>

      <div class="stepThree__radio">
        <p class="stepThree__radio-text">
          {{ $t('partners.multisport.steps.step3.hasPermits') }}
        </p>

        <RadioButtonController
          v-model="form.objectHasPermits"
          :radio-controls="hasPermits"
          :error="!!$v.objectHasPermits.$errors[0]?.$message"
        />
      </div>

      <div class="stepThree__radio">
        <p class="stepThree__radio-text">
          {{ $t('partners.multisport.steps.step3.hasSafetyEquipment') }}
        </p>

        <RadioButtonController
          v-model="form.objectHasSafetyEquipment"
          :radio-controls="hasSafetyEquipment"
          :error="!!$v.objectHasSafetyEquipment.$errors[0]?.$message"
        />
      </div>

      <div class="stepThree__radio">
        <p class="stepThree__radio-text">
          {{ $t('partners.multisport.steps.step3.hasReception') }}
        </p>

        <RadioButtonController
          v-model="form.objectHasReception"
          :radio-controls="hasReception"
          :error="!!$v.objectHasReception.$errors[0]?.$message"
        />

      </div>

      <div class="stepThree__radio">
        <p class="stepThree__radio-text">
          {{ $t('partners.multisport.steps.step3.isAutonomous') }}
        </p>

        <RadioButtonController
          v-model="form.objectIsAutonomous"
          :radio-controls="isAutonomous"
          :error="!!$v.objectIsAutonomous.$errors[0]?.$message"
        />
      </div>

      <div class="stepThree__requiredFields">
        {{ $t('partners.multisport.requiredFields') }}
      </div>

      <div>
        <BsCheckbox
          :model-value="allAgreementsSet ? true : checkAll"
          :label="checkboxesMain.label"
          :name="checkboxesMain.name"
          :value="checkboxesMain.value"
          @update:model-value="setAllAgreements($event)"
        />

        <UiExpander
          :text-to-show="$t('partners.multisport.seeMore')"
          :text-to-hide="$t('partners.multisport.seeLess')"
        >
          <div

            v-for="checkbox in checkboxesDetails"
            :key="checkbox.id"
            class="stepThree__checkbox"
          >
            <BsCheckbox
              v-model="form[checkbox.name as keyof typeof form]"
              :label="checkbox.label"
              :name="checkbox.name"
              :value="checkbox.value"
            />
          </div>
        </UiExpander>
      </div>

      <div>
        <BsCheckbox
          id="objectAcceptTerms"
          v-model="form.objectAcceptTerms"
          :value="form.objectAcceptTerms"
          :label="checkboxesTerms.label"
          :name="checkboxesTerms.name"
          :error="!!$v.objectAcceptTerms.$errors[0]?.$message"
          @update:model-value="form.objectAcceptPrivacyPolicy = $event"
        />
      </div>

      <ClientOnly>
        <Recaptcha />
      </ClientOnly>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.stepThree {

  &__title {
    @include h11;
    margin-bottom: 3.2rem;
    color: $text-500;
    text-align: center;
  }

  &__checkbox {
    padding-bottom: 1rem;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    padding-bottom: 1rem;
    background: transparent;
  }

  &__country,
  &__city,
  &__buildingNumber {
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    gap: 1.6rem;
  }

  &__city {
    flex-direction: row;
  }

  &__street {
    display: flex;
    flex-grow: 1;
    flex-direction: row;
    gap: 1.6rem;
    width: 100%;

    :deep(.bs-error) {
      flex: 1 1 0;
      min-width: 0;
    }

    :deep(.bs-input),
    :deep(.bs-select) {
      width: 100%;
    }
  }

  &__half {
    width: 50%;
  }

  &__quarter {
    width: 33%;
  }

  &__twoThirds {
    width: 66%;
  }

  &__phones {
    display: flex;
    flex-direction: row;
    gap: 1.6rem;

    &__phonePrefix {
      flex: 1;
    }

    &__phone {
      flex: 2;
    }
  }

  &__radio {
    margin: 1.6rem 0;

    &-text {
      @include h11;
      margin-bottom: 2rem;
      color: $text-500;
      text-align: center;
      text-wrap: pretty;
    }
  }

  &__requiredFields {
    @include body-4;
    padding: 3.2rem 0;
    color: $text-200;
    text-align: center;
  }

  @include atM {
    &__country,
    &__city,
    &__buildingNumber {
      flex-direction: row;
    }

    &__radio {
      &-text {
        text-align: left;
      }
    }

    &__requiredFields {
      text-align: left;
    }
  }

  @include atL {
    &__half {
      flex: 1;
      width: 50%;
    }

    &__phones {
      flex-direction: row;
      gap: 1.6rem;

      :deep(.bs-error) {
        flex: 1 1 0;
        min-width: 0;
      }

      :deep(.bs-input),
      :deep(.bs-select) {
        width: 100%;
      }
    }
  }
}
</style>
